/**
 * App slugs — code identifiers for assignable apps.
 * Used to map bucket ids (e.g. app_person_links) to UI components.
 *
 * person_links: Person Links app — guardians, siblings, TOR, counselors, supervisors, etc.
 *   Links users in context: user↔user (guardian, sibling) or ugp↔ugp (staff). Config per assignment metadata:
 *   - container_label_override: section title (e.g. "Guardians & Support")
 *   - enabled_relationship_types: string[] — which types appear in Add Person Link (default: all)
 *   - required_fields: string[] — extra required fields beyond first_name, last_name. e.g. ["email","phone"]
 *   - counselor_visible_properties: string[] — property keys to show on counselor person-link rows
 *     (resolved from Profile Properties on the linked UGP). e.g. ["meeting_link"]
 *   - show_counselor_meeting_link: boolean (legacy) — when true, same as counselor_visible_properties: ["meeting_link"]
 *
 * profile_properties: Profile Properties app — audience-scoped values on relationships (parent = owner UGP).
 *   Rows: source_slug=profile_properties, parent_table=user_groups_periods, child_table/child_id=audience,
 *   relationship_type=property key (e.g. meeting_link), value=scalar. Property keys are NOT global — another app
 *   may use the same relationship_type string under a different source_slug.
 *
 * person_links / group_hierarchy / form_assignment: other source_slug values on the same relationships table.
 *   Always filter by source_slug (and parent/child shape), not relationship_type alone.
 *   APP ASSIGNMENT METADATA:
 *   - container_label_override: section title on User Profile
 *   - enabled_properties: string[] — keys users may add (default meeting_link)
 *   - property_definitions: { [key]: { label, kind: "url"|"text" } }
 *   PAGE PERMISSIONS:
 *   - can_add_ugp_properties, can_edit_ugp_properties, can_remove_ugp_properties
 *
 *   ANALYTICS: Person links are stored in the relationships table. To count guardians,
 *   counselors, supervisors, etc., query relationships (not group membership). Example:
 *   SELECT COUNT(DISTINCT r.child_id) FROM relationships r
 *   WHERE r.relationship_type = 'guardian' AND (
 *     (r.parent_table = 'users' AND r.child_table = 'users')
 *     OR (r.parent_table = 'user_groups_periods' AND r.child_table IN ('user_groups_periods','users'))
 *   ) AND ...
 *
 * student_information: Student Information app — enrollment attributes for a ugp when that role is a student.
 *   Data lives in table student_info. One row per student ugp; key = user_groups_periods_id.
 *   Field keys, DB columns, UI control types, and default order: client/src/constants/studentInformationFields.js
 *
 *   DATES (single source of truth for enrollment window — not in student_info):
 *   - Do NOT add start_date/end_date to student_info. The enrollment window is the groups_periods date range
 *     (period’s start/end via ugp → groups_periods → period). ugp.start_date and ugp.end_date are optional overrides
 *     when set (e.g. override_period); when not set, the window is the period’s range.
 *   - enrollment_date — optional point-in-time "when they actually enrolled" (distinct from window start).
 *   - exit_date — point-in-time "when they actually left." Required when status is any ended state (e.g. Withdrawn, Exited, Completed).
 *     Do NOT assume period/ugp end = exit date; store exit_date explicitly. Status gives the meaning (withdrawal vs completion vs other).
 *
 *   STATUS (column name is status; covers full lifecycle including pre-enrolled if needed):
 *   - status e.g. 'Active' | 'Withdrawn' | 'Exited' | 'Completed'. When status is an ended state, exit_date is required.
 *     Status interprets exit_date for analytics: Withdrawn = withdrawal, Completed = completion, etc.
 *
 *   APP ASSIGNMENT METADATA (per assignment; if none or empty enabled_attributes, no optional attributes are used):
 *   - enabled_attributes: string[] — which student_info columns to show/use (see studentInformationFields.js).
 *   - exitdate_sync_popup: boolean (default true) — when true, User Profile may prompt when status moves to Withdrawn/Exited/Completed to confirm/set exit_date (wired in profile UI).
 *   - withdrawal_sync_ugp_end_date: boolean (legacy) — when true, PUT /api/student-info also sets ugp.end_date = exit_date when saving an ended status with exit_date.
 *   - masked_attributes: string[] — student_info fields to mask with click-to-reveal (if any are maskable in the field catalog).
 *   - grade_level_options: array (optional) — allowed values for grade_level for this assignment (e.g. K–12 vs numbered).
 *   - status_options: array (optional) — allowed values for status; default Active, Withdrawn, Exited, Completed when omitted.
 *   - vendor_allowed_gp_roots: string[] (optional) — limits vendor_groups_periods_id picker. Plain gp_id includes that team; *descendants*<gp_id> includes nested teams only (not the root unless also listed plainly).
 *
 *   VENDOR + SCHEDULE TRACK + PATHWAY (columns on student_info — 1:1 per ugp, not association rows):
 *   - vendor_groups_periods_id — FA / vendor partner team gp (work samples + vouchers).
 *   - track_period_id — scheduler period (type=track) for LP / schedule resolution.
 *   - primary_pathway — taxonomy.id (usually type=concentration) for the student's primary CTE pathway.
 *   - update_history — jsonb append-only when vendor, track, or pathway changes ({ at, by_user_id, changes }).
 *   Enable via enabled_attributes (e.g. vendor_groups_periods_id, track_period_id, primary_pathway) in Apps Manager.
 *
 *   PAGE PERMISSIONS (user-profiles page_configuration.metadata.configuration):
 *   - can_edit_student_information — edit/save Student Information section (independent of can_edit_user_data).
 *
 *   School / platform logins and API keys: use Credentials & Keys app (table credentials_keys), not student_info.
 *
 *   ANALYTICS: Enrollment window = groups_periods/period date range (or ugp.start_date/ugp.end_date when overridden). Actual events = enrollment_date, exit_date, status.
 *   Use status to interpret exit_date. Join student_info to user_groups_periods on student_info.user_groups_periods_id = ugp.id.
 *
 * credentials_and_keys: Credentials & Keys app — logins and opaque keys/tokens per enrollment (ugp).
 *   Data lives in table credentials_keys (many rows per user_groups_periods_id).
 *   entry_type: "credentials" — platform_label, title, email, username, password (text), notes.
 *   entry_type: "key" — platform_label, title, password column holds the token (UI: "Value"), optional notes.
 *   source_slug defaults to credentials_and_keys for rows owned by this app.
 *   APP ASSIGNMENT METADATA:
 *   - container_label_override: section title on User Profile.
 *   - credential_control: "locked_only" | "freeform_only" | "both"
 *     locked_only — user must pick platform from locked_platforms only (no custom names).
 *     freeform_only — platform is free-text only.
 *     both — choose from locked_platforms or enter a custom platform ("Other").
 *   - locked_platforms: string[] — e.g. ["Google Classroom","School LMS"]; used when credential_control is locked_only or both.
 *   - required_credentials: string[] | { title, entry_type? }[] — title match is case-insensitive. Omit entry_type (or plain string) = credential OR key; "credentials" or "key" = that type only. Textarea: plain title, or "credential: …", or "key: …"
 *
 *   PAGE PERMISSIONS (user-profiles page_configuration.metadata.configuration):
 *   - can_add_credentials, can_edit_credentials, can_remove_credentials
 *
 * custom_forms: Custom Forms app — forms assigned to the enrollment's group-period in the left column.
 *   Assign to groups_periods to show the section for users in that gp.
 *   relationship_type fill_out — inline expand/collapse in the Custom Forms section (profile attributes).
 *   relationship_type assignment — opens LMS-style assignment viewer in the column right of the profile column.
 *
 *   PAGE PERMISSIONS (user-profiles page_configuration.metadata.configuration):
 *   - custom_forms_submission_scope — own_profile_only | assigned_students | organization | platform
 *   - can_view_form — see assigned forms in the Custom Forms section
 *   - can_create_submission — start a new attempt (max attempts still apply)
 *   - can_view_submission — read existing responses (read-only when edit is not granted)
 *   - can_edit_draft — save and submit while status is draft
 *   - can_edit_submission — staff override: edit after submit (bypasses submit lock)
 *   - can_delete_submission — remove a submission
 *   - can_view_question_answers — see creator answer keys in the assignment viewer sidebar
 *   - can_update_points — manual grading on points-type forms (Apply points)
 *   - collaboration: { chat:{public,internal,private}, flags:{read,create,resolve,reopen,markAddressed,createWorkSample}, admin:{viewAllPrivateMessages} } — staff Chat + Flags capabilities
 *   ASSIGNMENT POLICY (forms.metadata.assignmentDefaults → relationships.metadata → submission.override_settings):
 *   - assignee_public_read — assignee may read public (Student) chat (default true)
 *   - assignee_public_write — assignee may post in public chat (default true)
 *   - assignee_flags_read — assignee may see flags (default true)
 *   - assignee_flags_mark_addressed — assignee may mark flags addressed (default true)
 *
 * attachments: Attachments app — links and uploaded files per enrollment (documents table).
 *   Data lives in table documents. Default parent: user_groups_periods (ugp.id).
 *   APP ASSIGNMENT METADATA:
 *   - container_label_override: section title on User Profile
 *   - allowed_categories: string[] — when set, Category field appears in UI (slug values, e.g. grad_plan)
 *   - allow_upload: boolean (default true) — when false, URL/link only (no file upload in UI)
 *   - required_attachments: string[] | { title }[] — titles that must exist on the profile (case-insensitive match on documents.title); missing slots show as red placeholders
 *   Document lifecycle (documents.status): active (UI), deleted (soft remove), replaced (superseded; replaced_by_id → new row)
 *
 *   Form file uploads use documents with source_slug=form_uploads, parent_table=form_submissions,
 *   metadata.question_id; file uploads are resolved from documents on read (not stored in answers).
 *
 *   PAGE PERMISSIONS (user-profiles page_configuration.metadata.configuration):
 *   - can_add_attachments, can_edit_attachments, can_remove_attachments
 *
 * payments: Payments app — payment tracking per student (ugp) via an event ledger. Three layers:
 *   1. App assignment (this slug on a groups_periods) → the Payments section shows on profiles.
 *   2. Payment schedule (the charge) — relationships rows on the group-period (source_slug='payments',
 *      relationship_type='monthly_payment'); label/amount/cadence/due date/tracking_mode/display live in
 *      relationships.metadata and are inherited by EVERY student in that gp (new enrollments included).
 *   3. payment_transactions table — an append-style ledger of money events per student
 *      (payment|refund|adjustment|failed_attempt|waiver), referencing the charge via requirement_id.
 *      Status is DERIVED from the ledger + tracking_mode; a charge with no settling event is Unpaid.
 *   Derived statuses: unpaid | partial | paid | refunded | not_applicable | payment_failure | insufficient_funds.
 *   Admin: set the schedule on the standalone Payments page (/payments). API: /api/payments.
 *   PAGE PERMISSIONS (user-profiles page_configuration.metadata.configuration):
 *   - can_edit_payments — record transactions / quick-set statuses on the profile Payments section.
 *
 * associations: Associations app — link a profile enrollment (UGP) to a group-period without membership.
 *   Rows: source_slug=associations, parent=user_groups_periods, child=groups_periods,
 *   relationship_type = association kind (vendor_partner, program_site, track).
 *   APP ASSIGNMENT METADATA:
 *   - container_label_override: section title on User Profile
 *   - enabled_association_types: { type, label, allowed_gp_roots? }[] — which kinds may be added
 *
 *   PAGE PERMISSIONS (user-profiles page_configuration.metadata.configuration):
 *   - can_add_associations, can_remove_associations — Associations section on profile (vendor track, etc.)
 *
 * work_samples: Work Sample & Compliance Workflow (org-level switch). Vendor partner from
 *   student_info.vendor_groups_periods_id; vouchers snapshot vendor_groups_periods_id.
 *
 * ALL_USERS: Sentinel UUID for "all users" assignments. Use in relationships:
 *   parent_table='users', parent_id='00000000-0000-0000-0000-000000000000'
 */
export const APP_SLUGS = {
  PERSON_LINKS: 'person_links',
  STUDENT_INFORMATION: 'student_information',
  CREDENTIALS_AND_KEYS: 'credentials_and_keys',
  CUSTOM_FORMS: 'custom_forms',
  ATTACHMENTS: 'attachments',
  PROFILE_PROPERTIES: 'profile_properties',
  PAYMENTS: 'payments',
  ASSOCIATIONS: 'associations',
  WORK_SAMPLES: 'work_samples'
};

export const ALL_USERS_SENTINEL = '00000000-0000-0000-0000-000000000000';

export const APP_SLUG_TO_LABEL = {
  [APP_SLUGS.PERSON_LINKS]: 'Person Links (Guardians, Siblings, TOR, Counselors, Supervisors)',
  [APP_SLUGS.STUDENT_INFORMATION]: 'Student Information',
  [APP_SLUGS.CREDENTIALS_AND_KEYS]: 'Credentials & Keys',
  [APP_SLUGS.CUSTOM_FORMS]: 'Custom Forms',
  [APP_SLUGS.ATTACHMENTS]: 'Attachments',
  [APP_SLUGS.PROFILE_PROPERTIES]: 'Profile Properties',
  [APP_SLUGS.PAYMENTS]: 'Payments',
  [APP_SLUGS.ASSOCIATIONS]: 'Associations',
  [APP_SLUGS.WORK_SAMPLES]: 'Work Samples'
};

/** Concise admin copy: what the app does and where it surfaces (Apps Manager, tooltips). */
export const APP_SLUG_DETAILS = {
  [APP_SLUGS.PERSON_LINKS]: {
    description:
      'Connects related people — guardians and siblings (user→user, all periods) or staff links such as teacher of record, counselor, or supervisor (ugp↔ugp on the student enrollment). Teacher of record and supervisor require the same organization and a compatible period; counselor requires the same organization only. Assignment metadata can limit which relationship types are offered via enabled_relationship_types.',
    appearsOn:
      'User Profile for people in assigned group-periods when Page Permissions allow this app; section labels can be overridden per assignment (relationship metadata).'
  },
  [APP_SLUGS.STUDENT_INFORMATION]: {
    description:
      'Stores enrollment and student attributes (student_info): grade, lifecycle status, enrollment/exit dates, and related flags.',
    appearsOn:
      'Student enrollment / profile UI for members of assigned group-periods when enabled; which fields appear depends on assignment metadata (enabled_attributes, masking, etc.).'
  },
  [APP_SLUGS.CREDENTIALS_AND_KEYS]: {
    description:
      'Stores logins or keys per enrollment (credentials_keys): platform, optional title, credentials (email/username/password/notes) or key (value + notes).',
    appearsOn:
      'User Profile left column when assigned; credential_control, locked_platforms, and required_credentials (by title) control platform pickers and missing placeholders.'
  },
  [APP_SLUGS.CUSTOM_FORMS]: {
    description: 'Configurable forms (title + questions) assigned to group-periods for profile fill-out.',
    appearsOn:
      'Left-column forms area for users tied to assigned group-periods when this app is shown for that context.'
  },
  [APP_SLUGS.ATTACHMENTS]: {
    description:
      'Stores links and uploaded files per enrollment (documents): grad plan, agreements, medical forms, Drive links, PDFs, etc.',
    appearsOn:
      'User Profile left column when assigned; allowed_categories and allow_upload come from assignment metadata.'
  },
  [APP_SLUGS.PROFILE_PROPERTIES]: {
    description:
      'Audience-scoped profile values per enrollment (relationships EAV): meeting links for students, staff, or everyone.',
    appearsOn:
      'User Profile when assigned to a group-period; Person Links can display resolved values on counselor rows.'
  },
  [APP_SLUGS.PAYMENTS]: {
    description:
      'Payment tracking per student via an event ledger (payment_transactions: payments, refunds, etc.). The schedule (charges) is set on the group-period (relationships) and inherited by every student; status is derived (Unpaid, Partial, Paid, Refunded, Not Applicable, Payment Failure, Insufficient Funds).',
    appearsOn:
      'User Profile left column when assigned, and the standalone Payments page (/payments) for the whole group-period roster. Configure the schedule on /payments.'
  },
  [APP_SLUGS.ASSOCIATIONS]: {
    description:
      'Links a profile enrollment to a group-period without membership — vendor partner track (FA01/FA02), program site, or schedule/track. Used by Work Samples for vendor_partner; other workflows can read the same rows.',
    appearsOn:
      'User Profile when assigned to the student (or staff) group-period. Configure enabled association types per assignment in Apps Manager.'
  },
  [APP_SLUGS.WORK_SAMPLES]: {
    description:
      'Work Sample & Compliance Workflow. Curates student form submissions into a per–Learning-Period portfolio, runs VDM review with a flag tracker, and produces dual-approved vouchers. Org switch on Permanent gp; TOR via person_links; vendor track via Associations (vendor_partner).',
    appearsOn:
      'Work-sample / compliance pages (TOR capture, VDM tracker, voucher approval). The org-level assignment is read by these pages, not the per-profile app list.'
  }
};

export function getAppSlugDetails(slug) {
  const key = String(slug ?? '')
    .trim()
    .toLowerCase();
  if (APP_SLUG_DETAILS[key]) return APP_SLUG_DETAILS[key];
  return {
    description:
      'This slug is registered in Apps Manager; behavior depends on how it is wired in the product code.',
    appearsOn:
      'Visibility is controlled by Page Permissions and UI routing—commonly User Profile, enrollment views, or contextual panels.'
  };
}

/** Ordered catalog entries for Apps Manager — slug is fixed in code; title is the default display name. */
export function listKnownAppDefinitions() {
  return Object.values(APP_SLUGS).map((slug) => ({
    slug,
    defaultTitle: APP_SLUG_TO_LABEL[slug] || slug,
    ...getAppSlugDetails(slug)
  }));
}

/** Student field keys, DB mapping, UI types — single source: studentInformationFields.js */
export {
  DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES,
  STUDENT_INFORMATION_ATTRIBUTE_KEYS,
  STUDENT_INFO_MASKABLE_ATTRIBUTE_KEYS,
  STUDENT_INFORMATION_FIELD_DEFS,
  STUDENT_INFORMATION_FIELD_KEYS,
  STUDENT_INFORMATION_TABLE,
  STUDENT_INFO_ASSIGNMENT_META,
  getStudentInformationFieldDef,
  normalizeStudentEnabledAttributes
} from './studentInformationFields';

export const PERSON_LINKS_REQUIRED_FIELD_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' }
];
